// frontend/src/pages/RequisitionEdit.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import apiClient from "../api/apiClient";
import LoadingSpinner from "../components/UI/LoadingSpinner";
import RequisitionEditWizard from "../components/Requisitions/edit/RequisitionEditWizard";
import { displayStatus } from "../components/Requisitions/edit/utils";
import {
  getRequisitionReadOnlyReason,
  isRequisitionEditHardDenied,
} from "../utils/userPermissions";

export default function RequisitionEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hardDenied, setHardDenied] = useState(false);
  const [hardDeniedMessage, setHardDeniedMessage] = useState("");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setData(null);
      setHardDenied(false);
      setHardDeniedMessage("");
      setLoadError("");

      try {
        const resp = await apiClient.get(`/requisitions/${id}/`);
        if (cancelled) return;

        const payload = resp.data || {};

        if (isRequisitionEditHardDenied(payload)) {
          setHardDenied(true);
          setHardDeniedMessage(
            "No tienes permisos para acceder a la edición de esta requisición."
          );
          return;
        }

        setData(payload);
      } catch (e) {
        console.error(e);

        if (!cancelled) {
          setLoadError("No se pudo cargar la información de la requisición.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const pageBlockedMessage = useMemo(() => {
    if (!data) return "";

    const readOnlyReason = getRequisitionReadOnlyReason(data);
    if (!readOnlyReason) return "";

    if (readOnlyReason === "status_locked") {
      const statusLabel = displayStatus(data?.status || "registered");
      return `Esta requisición se abrió en modo bloqueado. Solo puedes modificar requisiciones con estatus Registrado. Estatus actual: ${statusLabel}.`;
    }

    return "Esta requisición se abrió en modo solo lectura.";
  }, [data]);

  const handleSaved = () => {
    navigate("/requisitions");
  };

  if (loading) return <LoadingSpinner />;

  if (hardDenied) {
    return (
      <div className="p-4 md:p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow p-6">
          <div className="border border-red-200 bg-red-50 text-red-800 rounded p-4">
            <div className="font-semibold">Acceso denegado</div>
            <div className="mt-1">
              {hardDeniedMessage || "No tienes permisos para acceder a esta pantalla."}
            </div>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => navigate("/requisitions")}
              className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300"
            >
              Volver a requisiciones
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="p-4 md:p-6">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow p-6">
          <div className="border border-amber-200 bg-amber-50 text-amber-900 rounded p-4">
            {loadError || "No se pudo cargar la información de la requisición."}
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => navigate("/requisitions")}
              className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300"
            >
              Volver a requisiciones
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {!!pageBlockedMessage && (
        <div className="max-w-5xl mx-auto mb-4 border border-amber-300 bg-amber-50 text-amber-900 rounded p-4 text-sm">
          <div className="font-semibold">Modo bloqueado</div>
          <div className="mt-1">{pageBlockedMessage}</div>
        </div>
      )}

      <RequisitionEditWizard requisition={data} onSaved={handleSaved} />
    </div>
  );
}